import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";
import {
  useMeshPluginContext,
  MeshPluginActions,
} from "@services/providers/MeshPlugin";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import {
  Trash,
  CirclePlus,
  RotateCw,
  Copy,
  CheckCheck,
  TriangleAlert,
} from "lucide-react";
import { Button } from "@components/ui/button";
import { DropdownOption, FieldItem, fieldTypes } from "./allData";
import CustomSelect from "@components/common/CustomSelect";
import DropdownNodeEditor from "./DropdownNodeEditor";
import { toast } from "@components/ui/use-toast";
import ConfigDropdownNodeEditor from "./ConfigDropdownNodeEditor";
import InfoTooltip from "@components/common/InfoTooltip";

type Props = {
  dropdownOptions?: { label: string; value: string }[];
  setDropdownOptions?: React.Dispatch<
    React.SetStateAction<{ label: string; value: string }[]>
  >;
  dropdownInput?: {
    label: string;
    value: string;
    target?: string;
    resValues?: string;
  };
  setDropdownInput?: React.Dispatch<
    React.SetStateAction<{
      label: string;
      value: string;
      target?: string;
      resValues?: string;
    }>
  >;
  handleAddField?: () => void;
  handleRemoveDropdownOption?: (index: number) => void;
  handleAddDropdownOption?: () => void;
  dependsOnField?: string;
  setDependsOnField?: React.Dispatch<React.SetStateAction<string>>;
  definedVariables?: any[];
  prefillData?: FieldItem;
  isApiValue?: boolean;
  setIsApiValue?: React.Dispatch<React.SetStateAction<boolean>>;
  isFromVariable?: boolean;
  setIsFromVariable?: React.Dispatch<React.SetStateAction<boolean>>;
  editIndex?: number | null;
  setEditIndex?: React.Dispatch<React.SetStateAction<number | null>>;
  open?: boolean;
  handleClose?: () => void;
  logicType?: any[];
  isFromAnomaly?: boolean;
  setIsFromAnomaly?: React.Dispatch<React.SetStateAction<boolean>>;
  mainDropdownOptions?: DropdownOption[];
  setMainDropdownOptions?: React.Dispatch<
    React.SetStateAction<DropdownOption[]>
  >;
  targetDropdownMap?: { [targetField: string]: DropdownOption[] };
  setTargetDropdownMap?: React.Dispatch<
    React.SetStateAction<{ [targetField: string]: DropdownOption[] }>
  >;
  dropdownBlocks?: any[];
  setDropdownBlocks?: React.Dispatch<React.SetStateAction<any[]>>;
  alldropdownTrees?: { dropdownTrees: any[][]; configDropdownTrees: any[][] };
  setAlldropdownTrees?: React.Dispatch<
    React.SetStateAction<{
      dropdownTrees: any[][];
      configDropdownTrees: any[][];
    }>
  >;
  fields?: FieldItem[];
  configValue?: any;
  setConfigValue?: React.Dispatch<React.SetStateAction<any>>;
  handleRefreshAllVariables?: (selectedConfig?: any) => void;
  configDropdownTree?: any[][];
  submissionAction?: {
    submitBundleId: string;
    submitRequest: string;
    Ismultiple?: boolean;
    defaultActionField?: string;
    selectedFieldOption?: string;
    dependencyConfigs?: Array<{
      fieldOption: string;
      submitBundleId: string;
      submitRequest: string;
    }>;
  };
  setSubmissionAction?: React.Dispatch<
    React.SetStateAction<{
      submitBundleId: string;
      submitRequest: string;
      Ismultiple?: boolean;
      defaultActionField?: string;
      selectedFieldOption?: string;
      dependencyConfigs?: Array<{
        fieldOption: string;
        submitBundleId: string;
        submitRequest: string;
      }>;
    }>
  >;
  meshVersion?: string;
  actionBundlesOrWorkflows?: any[];
  settingsBundlesOrWorkflows?: any[];
  pluginId?: string;
};

const CreateActionField: React.FC<Props> = ({
  dropdownOptions,
  setDropdownOptions,
  dropdownInput,
  setDropdownInput,
  handleAddField,
  handleRemoveDropdownOption,
  definedVariables,
  prefillData,
  isApiValue,
  setIsApiValue,
  isFromVariable,
  setIsFromVariable,
  editIndex,
  setEditIndex,
  open,
  handleClose,
  logicType,
  isFromAnomaly,
  setIsFromAnomaly,
  alldropdownTrees,
  setAlldropdownTrees,
  configValue,
  setConfigValue,
  fields,
  handleRefreshAllVariables,
  submissionAction,
  setSubmissionAction,
  meshVersion,
  actionBundlesOrWorkflows,
  settingsBundlesOrWorkflows,
  pluginId,
}) => {
  const meshPluginContext = useMeshPluginContext();
  const { fieldName, fieldType, inputDefaultValue, savedSettings } =
    meshPluginContext;
  const contextDispatch = meshPluginContext.dispatch;
  // const [dependentField, setDependentField] = React.useState(false)
  const [selectedVariableName, setSelectedVariableName] = React.useState("");
  const [availableKeys, setAvailableKeys] = React.useState<string[]>([]);
  const [labelKey, setLabelKey] = React.useState("");
  const [valueKey, setValueKey] = React.useState("");
  const [selectedBundleId, setSelectedBundleId] = React.useState<string | null>(
    null,
  );
  const [selectedAnomalyDataType, setSelectedAnomalyDataType] =
    React.useState<string>("");
  const [selectedSource, setSelectedSource] = React.useState<
    "select" | "default" | "anomaly" | "variable" | "api" | "settings"
  >("select");
  const [defaultDropdownOptions, setDefaultDropdownOptions] =
    React.useState<boolean>(false);
  const [configDropdownTrees, setConfigDropdownTrees] = React.useState<any[][]>(
    [],
  );
  const [showConfigSelector, setShowConfigSelector] = React.useState(false);
  const [selectedConfigForRefresh, setSelectedConfigForRefresh] =
    React.useState<string>("");

  // Use workflows for mesh version 2.0, otherwise use bundles
  const actionBundles = React.useMemo(() => {
    if (meshVersion === "2.0") {
      return actionBundlesOrWorkflows || [];
    }
    const actionLogic: any = logicType?.find((l) => l.name === "Action");
    const bundles = actionLogic?.data || [];
    // Normalize bundles to ensure they have bundleId and bundleTitle properties
    return bundles.map((bundle: any) => ({
      ...bundle,
      bundleId: bundle.bundleId || bundle.id,
      bundleTitle:
        bundle.bundleTitle ||
        bundle.title ||
        `Bundle ${bundle.bundleId || bundle.id}`,
    }));
  }, [meshVersion, actionBundlesOrWorkflows, logicType]);

  const settingsBundles = React.useMemo(() => {
    if (meshVersion === "2.0") {
      return settingsBundlesOrWorkflows || [];
    }
    const settingsLogic: any = logicType?.find((l) => l.name === "Settings");
    const bundles = settingsLogic?.data || [];
    // Normalize bundles to ensure they have bundleId and bundleTitle properties
    return bundles.map((bundle: any) => ({
      ...bundle,
      bundleId: bundle.bundleId || bundle.id,
      bundleTitle:
        bundle.bundleTitle ||
        bundle.title ||
        `Bundle ${bundle.bundleId || bundle.id}`,
    }));
  }, [meshVersion, settingsBundlesOrWorkflows, logicType]);

  const settingsConfigurationOptions = React.useMemo(() => {
    if (!savedSettings || Object.keys(savedSettings).length === 0) return [];

    const options: { label: string; value: string }[] = [];
    Object.entries(savedSettings).forEach(([, formArray]: [string, any[]]) => {
      formArray.forEach((form) => {
        const value = form?.title || form?.uid;
        if (value) {
          options.push({ label: value, value });
        }
      });
    });

    const seen = new Set<string>();
    return options.filter((opt) => {
      if (seen.has(opt.value)) return false;
      seen.add(opt.value);
      return true;
    });
  }, [savedSettings]);

  const hasSettingsWorkflows = settingsConfigurationOptions.length > 0;

  const dependencyActionFields = React.useMemo(() => {
    return (fields || []).filter((f) => {
      if (f.fieldType !== "dropdown") {
        return false;
      }

      const hasApiDropdownTree =
        Array.isArray((f as any).dropdownTree) &&
        (f as any).dropdownTree.length > 0;
      const hasConfigDropdownTree =
        Array.isArray((f as any).configDropdownTree) &&
        (f as any).configDropdownTree.length > 0;

      const isSpecialSource =
        !!(f as any).fromAnomaly ||
        !!(f as any).fromVariable ||
        !!(f as any).isApiValue ||
        !!(f as any).isConfig ||
        hasApiDropdownTree ||
        hasConfigDropdownTree;

      // Only allow default/manual dropdown fields for dependency submit mode
      return !isSpecialSource;
    });
  }, [fields]);

  const sourceOptions = [
    { value: "default", label: "Default dropdown" },
    { value: "anomaly", label: "From anomaly fields" },
    {
      value: "api",
      label:
        meshVersion === "2.0"
          ? "Api request on select (Workflow)"
          : "Api request on select",
    },
    {
      value: "settings",
      label:
        meshVersion === "2.0"
          ? "Api request on configuration (Settings Workflow)"
          : "Api request on configuration (settings) ",
    },
  ];

  const resetDropdownRelatedState = () => {
    setSelectedSource("select");
    setDropdownOptions?.([]);
    setDropdownInput?.({ label: "", value: "" });
    setSelectedBundleId(null);
    setSelectedVariableName("");
    setAvailableKeys([]);
    setLabelKey("");
    setValueKey("");
    setSelectedAnomalyDataType("");
    setIsFromAnomaly?.(false);
    setIsFromVariable?.(false);
    setIsApiValue?.(false);
    setConfigValue?.(null);
    setConfigDropdownTrees([]);
    setAlldropdownTrees?.({ dropdownTrees: [], configDropdownTrees: [] });
    setDefaultDropdownOptions(false);
  };

  const resetSourceDependentSelections = () => {
    setDropdownOptions?.([]);
    setDropdownInput?.({ label: "", value: "" });
    setSelectedBundleId(null);
    setSelectedVariableName("");
    setAvailableKeys([]);
    setLabelKey("");
    setValueKey("");
    setSelectedAnomalyDataType("");
    setAlldropdownTrees?.({ dropdownTrees: [], configDropdownTrees: [] });
    setConfigDropdownTrees([]);
  };

  const extractKeysFromData = (data: any): string[] => {
    if (!Array.isArray(data) || data.length === 0) return [];

    // Try first item keys
    const firstItem = data.find(
      (item) =>
        typeof item === "object" && item !== null && !Array.isArray(item),
    );
    if (firstItem) {
      return Object.keys(firstItem);
    }

    // Otherwise, fallback to recursive key extraction
    const keys = new Set<string>();
    const walk = (item: any) => {
      if (Array.isArray(item)) item.forEach(walk);
      else if (typeof item === "object" && item !== null) {
        Object.keys(item).forEach((k) => keys.add(k));
        Object.values(item).forEach(walk);
      }
    };
    walk(data);
    return Array.from(keys);
  };

  // When dropdown changes:
  const handleSourceChange = (
    value: "select" | "default" | "anomaly" | "variable" | "api" | "settings",
  ) => {
    resetSourceDependentSelections();
    setSelectedSource(value);
    setIsFromAnomaly?.(value === "anomaly");
    setIsFromVariable?.(value === "variable");
    setIsApiValue?.(value === "api");
    setDefaultDropdownOptions?.(value === "default");
    setConfigValue?.(value === "settings");
  };

  // Prefill form when editing
  React.useEffect(() => {
    if (prefillData && editIndex !== null) {
      // Set basic fields
      contextDispatch({
        type: MeshPluginActions.SET_FIELD_NAME,
        payload: prefillData.fieldName,
      });
      contextDispatch({
        type: MeshPluginActions.SET_FIELD_TYPE,
        payload: prefillData.fieldType,
      });
      contextDispatch({
        type: MeshPluginActions.SET_INPUT_DEFAULT_VALUE,
        payload: prefillData.defaultValue ?? "",
      });

      // Determine dropdown source and set data
      const hasDropdownTree =
        prefillData.dropdownTree &&
        Array.isArray(prefillData.dropdownTree) &&
        prefillData.dropdownTree.length > 0;

      const hasConfigDropdownTree =
        prefillData.configDropdownTree &&
        Array.isArray(prefillData.configDropdownTree) &&
        prefillData.configDropdownTree.length > 0;

      const hasDropdownOptions =
        prefillData.dropdownOptions &&
        Array.isArray(prefillData.dropdownOptions) &&
        prefillData.dropdownOptions.length > 0;

      //  Config Dropdown (Settings)
      if (hasConfigDropdownTree || prefillData.isConfig) {
        //  Ensure proper structure with nested targetFields
        const formattedTrees: any =
          prefillData?.configDropdownTree?.map((rootNode: any) => {
            return [
              {
                ...rootNode,
                label: rootNode.label || "",
                value: rootNode.value || "",
                targetFields:
                  rootNode.targetFields?.map((tf: any) => ({
                    ...tf,
                    fieldName: tf.fieldName || "",
                    sourceType: tf.sourceType || "bundle",
                    bundleId: tf.bundleId || "",
                    variableName: tf.variableName || "",
                    labelKey: tf.labelKey || "",
                    valueKey: tf.valueKey || "",
                    availableKeys: tf.availableKeys || [],
                    defaultOptions: tf.defaultOptions || [],
                    previewOptions:
                      tf.previewOptions?.map((opt: any) => ({
                        ...opt,
                        label: opt.label || "",
                        value: opt.value || "",
                        targetFields: opt.targetFields || [],
                        children: opt.children || [],
                      })) || [],
                    commonApiConfig: tf.commonApiConfig || undefined,
                  })) || [],
              },
            ];
          }) || [];

        setConfigDropdownTrees(formattedTrees);
        setAlldropdownTrees?.({
          dropdownTrees: [],
          configDropdownTrees: formattedTrees,
        });
        setDropdownOptions?.([]); // Clear manual options
        setSelectedSource("settings");
        setIsApiValue?.(false);
        setIsFromVariable?.(false);
        setIsFromAnomaly?.(false);
        setConfigValue?.(true);
        setDefaultDropdownOptions?.(false);
      }
      //  API Dropdown (Bundle)
      else if (hasDropdownTree || prefillData.isApiValue) {
        const formattedTrees: any =
          prefillData?.dropdownTree?.map((rootNode: any) => {
            return [rootNode]; // Wrap each root in array
          }) || [];

        setAlldropdownTrees?.({
          dropdownTrees: formattedTrees,
          configDropdownTrees: [],
        });
        setDropdownOptions?.([]); // Clear manual options
        setSelectedSource("api");
        setIsApiValue?.(true);
        setIsFromVariable?.(false);
        setIsFromAnomaly?.(false);
        setConfigValue?.(null);
        setDefaultDropdownOptions?.(false);
      }
      //  Anomaly Dropdown
      else if (prefillData.fromAnomaly && hasDropdownOptions) {
        const anomalyValues =
          prefillData.dropdownOptions?.map((opt: any) => opt.value) || [];
        setSelectedAnomalyDataType(String(anomalyValues[0] || ""));
        setDropdownOptions?.(prefillData.dropdownOptions || []);
        setAlldropdownTrees?.({ dropdownTrees: [], configDropdownTrees: [] });
        setSelectedSource("anomaly");
        setIsApiValue?.(false);
        setIsFromVariable?.(false);
        setIsFromAnomaly?.(true);
        setConfigValue?.(null);
        setDefaultDropdownOptions?.(false);
      }
      //  Variable Dropdown
      else if (prefillData.fromVariable && hasDropdownOptions) {
        setDropdownOptions?.(prefillData.dropdownOptions || []);
        setAlldropdownTrees?.({ dropdownTrees: [], configDropdownTrees: [] });
        setSelectedSource("variable");
        setIsApiValue?.(false);
        setIsFromVariable?.(true);
        setIsFromAnomaly?.(false);
        setConfigValue?.(null);
        setDefaultDropdownOptions?.(false);
      }
      //  Default/Manual Dropdown
      else if (hasDropdownOptions) {
        setDropdownOptions?.(prefillData.dropdownOptions || []);
        setAlldropdownTrees?.({ dropdownTrees: [], configDropdownTrees: [] });
        setSelectedSource("default");
        setIsApiValue?.(false);
        setIsFromVariable?.(false);
        setIsFromAnomaly?.(false);
        setConfigValue?.(null);
        setDefaultDropdownOptions?.(true);
      }
      //  No dropdown data
      else {
        setDropdownOptions?.([]);
        setAlldropdownTrees?.({ dropdownTrees: [], configDropdownTrees: [] });
        setSelectedSource("select");
        setIsApiValue?.(false);
        setIsFromVariable?.(false);
        setIsFromAnomaly?.(false);
        setConfigValue?.(null);
        setDefaultDropdownOptions?.(false);
      }

      //  Handle submit button
      if (prefillData.fieldType === "submit" && prefillData?.submitConfig) {
        const config = prefillData.submitConfig;

        if (
          config.Ismultiple &&
          config.submit_types &&
          config.submit_types.length > 0
        ) {
          // Dependency mode
          const dependencyConfigs = config.submit_types.map((item: any) => ({
            fieldOption: item.option_given_name || "",
            submitBundleId: item.bundleId || "",
            submitRequest: item.requestId || "",
          }));

          setSubmissionAction?.({
            Ismultiple: true,
            defaultActionField: config.submit_types[0]?.field_given_name || "",
            selectedFieldOption: "",
            dependencyConfigs: dependencyConfigs,
            submitBundleId: "",
            submitRequest: "",
          });
        } else {
          // Normal mode
          setSubmissionAction?.({
            Ismultiple: false,
            submitBundleId: config.bundleId || "",
            submitRequest: config.requestId || "",
            defaultActionField: "",
            selectedFieldOption: "",
            dependencyConfigs: [],
          });
        }
      } else if (prefillData.fieldType === "submit") {
        // Reset submission action if submit type but no config
        setSubmissionAction?.({
          submitBundleId: "",
          submitRequest: "",
          Ismultiple: false,
          defaultActionField: "",
          selectedFieldOption: "",
          dependencyConfigs: [],
        });
      }

      setDropdownInput?.({ label: "", value: "" });
    } else if (editIndex === null) {
      //  Reset form for new field

      contextDispatch({ type: MeshPluginActions.SET_FIELD_NAME, payload: "" });
      contextDispatch({
        type: MeshPluginActions.SET_FIELD_TYPE,
        payload: "select",
      });
      contextDispatch({
        type: MeshPluginActions.SET_INPUT_DEFAULT_VALUE,
        payload: "",
      });
      setDropdownOptions?.([]);
      setAlldropdownTrees?.({ dropdownTrees: [], configDropdownTrees: [] });
      setSelectedSource("select");
      setIsFromAnomaly?.(false);
      setIsFromVariable?.(false);
      setIsApiValue?.(false);
      setDropdownInput?.({ label: "", value: "" });
      setConfigValue?.(null);
      setConfigDropdownTrees([]);
      setDefaultDropdownOptions?.(false);
      setSubmissionAction?.({
        submitBundleId: "",
        submitRequest: "",
        Ismultiple: false,
        defaultActionField: "",
        selectedFieldOption: "",
        dependencyConfigs: [],
      });
    }
  }, [prefillData, editIndex, setSubmissionAction]);

  // updating the selected options in the custom select according to the dropdownoptions when its from anomaly
  React.useEffect(() => {
    if (isFromAnomaly && dropdownOptions) {
      const temp = dropdownOptions?.map((opt) => opt.value) || [];
      setSelectedAnomalyDataType(String(temp[0] || ""));
    }
  }, [dropdownOptions, isFromAnomaly]);

  // Normalize data into array of flat objects
  const normalizeToObjectsArray = (input: any): any[] => {
    const result: any[] = [];

    const walk = (data: any) => {
      if (Array.isArray(data)) {
        data.forEach(walk);
      } else if (data && typeof data === "object") {
        const flat: any = {};
        const flatten = (obj: any, prefix = "") => {
          Object.entries(obj).forEach(([k, v]) => {
            const key = prefix ? `${prefix}.${k}` : k;
            if (v && typeof v === "object" && !Array.isArray(v)) {
              flatten(v, key);
            } else {
              flat[key] = Array.isArray(v) ? v.join(", ") : v;
            }
          });
        };
        flatten(data);
        result.push(flat);
      }
    };

    walk(input);
    return result;
  };

  // Handle variable selection and extract keys
  const generateDropdownOptions = (
    rawData: any,
    labelKey?: string,
    valueKey?: string,
  ): { label: string; value: string }[] => {
    if (
      typeof rawData === "string" ||
      typeof rawData === "number" ||
      typeof rawData === "boolean"
    ) {
      const val = String(rawData);
      return [{ label: val, value: val }];
    }

    const normalized = normalizeToObjectsArray(rawData);
    const options: { label: string; value: string }[] = [];

    normalized.forEach((item) => {
      if (typeof item === "object" && item !== null) {
        if (
          labelKey &&
          valueKey &&
          Object.prototype.hasOwnProperty.call(item, labelKey) &&
          Object.prototype.hasOwnProperty.call(item, valueKey)
        ) {
          options.push({
            label: String(item[labelKey]),
            value: String(item[valueKey]),
          });
        } else {
          const keys = Object.keys(item);
          if (keys.length >= 2) {
            options.push({
              label: String(item[keys[0]]),
              value: String(item[keys[1]]),
            });
          } else if (keys.length === 1) {
            const key = keys[0];
            options.push({
              label: String(item[key]),
              value: String(item[key]),
            });
          }
        }
      }
    });

    return options;
  };

  // Handle variable selection and extract keys
  const handleSetDropdown = (variableName: string) => {
    setSelectedVariableName(variableName);
    setDropdownOptions?.([]);
    setLabelKey("");
    setValueKey("");

    const selectedVar = definedVariables?.find(
      (item: any) =>
        String(item.sourceBundleId) === selectedBundleId &&
        item.name === variableName,
    );
    if (!selectedVar) return;

    const raw = selectedVar.value;
    const name = selectedVar.name;
    if (
      typeof raw === "string" ||
      typeof raw === "number" ||
      typeof raw === "boolean"
    ) {
      setDropdownOptions?.([
        {
          label: String(name),
          value: String(raw),
        },
      ]);
      setAvailableKeys([]);
      return;
    }
    const keys = extractKeysFromData(raw);
    setAvailableKeys(keys);
  };

  // data types for isFromAnomaly
  const anomalyDataType =
    fieldTypes &&
    fieldTypes?.map((type: any) => ({ label: type, value: type }));

  // Handle selection from CustomSelect
  const handleSelectChange = (selectedItems: string | string[]) => {
    const normalized = Array.isArray(selectedItems)
      ? selectedItems
      : selectedItems
        ? [selectedItems]
        : [];

    setSelectedAnomalyDataType(String(normalized[0] || ""));

    const options = normalized.map((item: string) => ({
      label: item,
      value: item,
    }));
    setDropdownOptions?.(options);
  };

  // Add Dropdown Option
  const handleAddDropdownOption = () => {
    if (
      !dropdownInput?.label ||
      !dropdownInput.label.trim() ||
      !dropdownInput?.value ||
      !dropdownInput.value.trim()
    )
      return;

    const trimmedLabel = dropdownInput.label.trim();
    const trimmedValue = dropdownInput.value.trim();

    // Prevent duplicates
    if (
      dropdownOptions?.some(
        (opt) => opt.value === trimmedValue || opt.label === trimmedLabel,
      )
    ) {
      setDropdownInput?.({ label: "", value: "" });
      toast({
        variant: "destructive",
        title: "Duplicate Option",
        description: "This option already exists.",
      });
      return;
    }
    const newOption = {
      label: trimmedLabel,
      value: trimmedValue,
      target: dropdownInput.target || "",
      resValues: dropdownInput.resValues || "",
    };
    setDropdownOptions?.([...(dropdownOptions ?? []), newOption]);
    setDropdownInput?.({ label: "", value: "" });
  };

  const handleGenerateDropdownOptions = () => {
    const selectedVar = definedVariables?.find(
      (item: any) =>
        String(item.sourceBundleId) === selectedBundleId &&
        item.name === selectedVariableName,
    );
    if (!selectedVar) return;

    const options = generateDropdownOptions(
      selectedVar.value,
      labelKey,
      valueKey,
    );
    setDropdownOptions?.(options);
  };

  // Copy action key to clipboard
  const handleCopyAnomalyField = (opt: any, type: any) => {
    const currentType = type === "anomaly" ? "Anomaly Field" : "";

    const formattedValue =
      meshVersion === "2.0" ? `{{$action.${opt}}}` : `{{action.${opt}}}`;
    navigator.clipboard.writeText(formattedValue);
    toast({
      variant: "info",
      title: `${currentType} Copied!`,
      description: `The value "${formattedValue}" has been copied to your clipboard.`,
      duration: 3000,
    });
  };

  // Get all configurations as a flat array
  const getAllConfigurations = () => {
    if (!savedSettings || Object.keys(savedSettings).length === 0) return [];

    const allConfigs: any[] = [];
    Object.entries(savedSettings).forEach(
      ([tabId, formArray]: [string, any[]]) => {
        formArray.forEach((config) => {
          allConfigs.push({
            ...config,
            tabId, // Include tabId for reference
          });
        });
      },
    );
    return allConfigs;
  };

  // Handle refresh button click
  const handleRefreshClick = () => {
    const allConfigs = getAllConfigurations();

    if (allConfigs.length === 0) {
      toast({
        variant: "destructive",
        title: "No Configurations Found",
        description:
          "Please create at least one configuration before refreshing variables.",
        duration: 3000,
      });
      return;
    }

    if (allConfigs.length === 1) {
      const singleConfig = allConfigs[0];

      const didStart = handleRefreshAllVariables?.(singleConfig);

      if (didStart) {
        toast({
          variant: "default",
          title: "Variables Refreshing",
          description: `Variables refreshing using configuration: ${
            singleConfig.title || singleConfig.uid
          }`,
          duration: 3000,
        });
      }
    } else {
      // Multiple configs - show selector
      setShowConfigSelector(true);
    }
  };

  // Handle config selection and refresh
  const handleConfigSelectionAndRefresh = () => {
    if (!selectedConfigForRefresh) {
      toast({
        variant: "destructive",
        title: "No Configuration Selected",
        description: "Please select a configuration to proceed.",
        duration: 3000,
      });
      return;
    }

    const allConfigs = getAllConfigurations();
    const selectedConfig = allConfigs.find(
      (c) => c.uid === selectedConfigForRefresh,
    );

    if (selectedConfig) {
      handleRefreshAllVariables?.(selectedConfig);
      toast({
        variant: "default",
        title: "Variables Refreshed",
        description: `Using configuration: ${selectedConfig.title || selectedConfig.uid}`,
        duration: 3000,
      });
      setShowConfigSelector(false);
      setSelectedConfigForRefresh("");
    }
  };

  // Check if submit button in dependency mode is fully configured
  const isSubmitDependencyModeValid = () => {
    if (fieldType !== "submit" || !submissionAction?.Ismultiple) {
      return true; // Not in dependency mode, no special validation needed
    }

    // Must have a field selected
    if (!submissionAction?.defaultActionField) {
      return false;
    }

    // Get the selected field's options
    const selectedField = dependencyActionFields.find(
      (f) => f.fieldName === submissionAction?.defaultActionField,
    );
    const fieldOptions = selectedField?.dropdownOptions || [];

    // If no options in the field, can't configure
    if (fieldOptions.length === 0) {
      return false;
    }

    // Check if all options have bundle selected
    const allOptionsConfigured = fieldOptions.every((option: any) => {
      const config = submissionAction?.dependencyConfigs?.find(
        (c) => c.fieldOption === option.value,
      );
      return config && config.submitBundleId; // Bundle must be selected
    });

    return allOptionsConfigured;
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        // className={`overflow-y-auto text-sm ${isApiValue ? "max-w-[90%]" : "max-w-[40%]"} max-h-[90%] `}
        className={`overflow-y-auto text-sm ${
          fieldType === "dropdown" && (isApiValue || configValue)
            ? "max-w-[90%]"
            : "max-w-[40%]"
        } max-h-[90%]`}
        onInteractOutside={(event) => {
          event.preventDefault();
        }}
      >
        <DialogHeader className="border-b pb-3">
          <DialogTitle>
            <div className="flex justify-between items-center">
              <>{editIndex !== null ? "Edit Field" : "Add Field"}</>
              {meshVersion !== "2.0" && (
                <div
                  // className="flex items-center gap-1 cursor-pointer group text-primaryGreen text-sm mr-6 border py-2 px-3 rounded-md border-primaryGreen"
                  className="flex items-center gap-2 cursor-pointer text-sm mr-8 bg-primaryGreen  p-2 rounded-md text-background group"
                  onClick={handleRefreshClick}
                >
                  <RotateCw
                    size={14}
                    className="
                  group-hover:rotate-[360deg]
                  duration-300
                  font-light
                  group-hover:transition-transform
                  group-hover:duration-700
                  transition-none
                  "
                  />
                  <label className="font-normal">
                    Refresh Action Variables
                  </label>
                </div>
              )}
            </div>
          </DialogTitle>
        </DialogHeader>
        <div className="w-full space-y-4 border border-secondary p-4 rounded-md">
          <div className="flex w-full gap-4">
            <div className="flex flex-col gap-1 flex-1">
              <label>Field Name (label) *</label>
              <input
                type="text"
                placeholder="e.g. Action Name"
                value={fieldName}
                onChange={(e) =>
                  contextDispatch({
                    type: MeshPluginActions.SET_FIELD_NAME,
                    payload: e.target.value,
                  })
                }
                className="p-2 border rounded-md bg-transparent focus:ring-secondary focus:ring-1"
              />
            </div>

            <div className="flex flex-col gap-1 flex-1">
              <label>Field Type</label>
              <Select
                onValueChange={(
                  value:
                    | "select"
                    | "input"
                    | "dropdown"
                    | "import_settings"
                    | "submit",
                ) => {
                  resetDropdownRelatedState();
                  if (
                    fieldName?.trim().toLowerCase() === "submit" &&
                    value !== "submit"
                  ) {
                    contextDispatch({
                      type: MeshPluginActions.SET_FIELD_NAME,
                      payload: "",
                    });
                  }
                  contextDispatch({
                    type: MeshPluginActions.SET_FIELD_TYPE,
                    payload: value,
                  });
                }}
                value={fieldType}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="select">Select type</SelectItem>
                    {((editIndex != null &&
                      fields?.[editIndex]?.fieldType === "submit") ||
                      !fields?.some((f) => f?.fieldType === "submit")) && (
                      <SelectItem value="submit">
                        Action Submission Button
                      </SelectItem>
                    )}
                    <SelectItem value="dropdown">Dropdown</SelectItem>
                    {/* <SelectItem value="input">Input</SelectItem> */}
                    {((editIndex != null &&
                      fields?.[editIndex]?.fieldType === "import_settings") ||
                      !fields?.some(
                        (f) => f?.fieldType === "import_settings",
                      )) && (
                      <SelectItem value="import_settings">
                        Settings configuration
                      </SelectItem>
                    )}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Dropdown Option Inputs */}
          {fieldType === "dropdown" && (
            <div className="flex flex-col gap-1">
              {/* Dropdown conditions */}

              <div className="flex flex-col gap-1 mb-4 max-w-[400px]">
                <label htmlFor="sourceSelect" className="font-medium">
                  Select a condition
                </label>
                <Select
                  onValueChange={(value) =>
                    handleSourceChange(
                      value as
                        | "select"
                        | "default"
                        | "anomaly"
                        | "variable"
                        | "api"
                        | "settings",
                    )
                  }
                  value={selectedSource}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select condition" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="select">Choose a condition</SelectItem>
                      {sourceOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2 border p-2 rounded-md bg-card">
                {isFromVariable &&
                  definedVariables &&
                  definedVariables.length === 0 && (
                    <label className=" text-primary font-semibold">
                      No defined variables found.
                    </label>
                  )}

                {selectedSource == "select" && (
                  <p className="text-muted-foreground text-sm">
                    Please select a condition to proceed.
                  </p>
                )}

                {/* 1. Select a Bundle/Workflow */}
                {isFromVariable && actionBundles?.length > 0 && (
                  <>
                    <div className="mb-2">
                      <label className="text-sm font-medium mb-1">
                        {meshVersion === "2.0"
                          ? "Select Action Workflow"
                          : "Select Bundle"}
                      </label>
                      <Select
                        onValueChange={(bundleId) => {
                          setSelectedBundleId(bundleId);
                          setSelectedVariableName("");
                          setAvailableKeys([]);
                          setLabelKey("");
                          setValueKey("");
                        }}
                        value={selectedBundleId || ""}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue
                            placeholder={
                              meshVersion === "2.0"
                                ? "Select Workflow"
                                : "Select Bundle"
                            }
                          />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            {actionBundles?.map((bundle: any) => (
                              <SelectItem
                                key={bundle.bundleId}
                                value={String(bundle.bundleId)}
                              >
                                {bundle.bundleTitle ||
                                  `Bundle ${bundle.bundleId}`}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* 2. Select a Variable under the selected Bundle (only for mesh version 1.0) */}
                    {meshVersion !== "2.0" && selectedBundleId && (
                      <div>
                        <label className="text-sm font-medium mb-1">
                          Select Variable
                        </label>
                        <Select
                          onValueChange={(value) => handleSetDropdown(value)}
                          value={selectedVariableName}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select Variable" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectGroup>
                              {definedVariables
                                ?.filter(
                                  (v: any) =>
                                    v.sourceBundleId ===
                                    Number(selectedBundleId),
                                )
                                .map((variable: any) => (
                                  <SelectItem
                                    key={variable.name}
                                    value={variable.name}
                                  >
                                    {variable.name}
                                  </SelectItem>
                                ))}
                            </SelectGroup>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </>
                )}

                {isFromVariable &&
                  meshVersion !== "2.0" &&
                  availableKeys.length > 0 && (
                    <div className="mt-2 p-2 border rounded-md bg-background">
                      <label className=" m-1 text-muted-foreground font-semibold">
                        Select Dropdown Label and value.
                      </label>
                      <div className="flex flex-col gap-2 mt-3">
                        <div className="flex gap-2">
                          <div className="w-1/2">
                            <label className="text-sm m-1">Label Key</label>
                            <Select
                              onValueChange={setLabelKey}
                              value={labelKey}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select Label Key" />
                              </SelectTrigger>
                              <SelectContent>
                                {availableKeys.map((key, idx) => (
                                  <SelectItem key={idx} value={key}>
                                    {key}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="w-1/2">
                            <label className="text-sm m-1">Value Key</label>
                            <Select
                              onValueChange={setValueKey}
                              value={valueKey}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select Value Key" />
                              </SelectTrigger>
                              <SelectContent>
                                {availableKeys.map((key, idx) => (
                                  <SelectItem key={idx} value={key}>
                                    {key}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <Button
                          className="mt-2"
                          onClick={handleGenerateDropdownOptions}
                          disabled={!labelKey || !valueKey}
                        >
                          Generate Dropdown Options
                        </Button>
                      </div>
                    </div>
                  )}

                {/* is from anomalies */}

                {isFromAnomaly && (
                  <div className="flex flex-col gap-2 max-w-[400px] overflow-hidden">
                    <label className="text-sm font-medium">
                      Select Anomaly Type
                    </label>
                    <CustomSelect
                      data={anomalyDataType}
                      defaultValue={selectedAnomalyDataType}
                      placeholder="Select Anomaly Type"
                      onSelect={handleSelectChange}
                      multiSelect
                    />
                  </div>
                )}

                {/* on select section  */}

                {/* isApiValue ========== truthy */}

                {/*  API Dropdown - Show all root options */}
                {!isFromVariable && !isFromAnomaly && isApiValue && (
                  <div className="space-y-2">
                    {alldropdownTrees?.dropdownTrees?.map(
                      (nodeWrapper, idx) => {
                        // Handle both wrapped [[node]] and flat [node] structures
                        const node = Array.isArray(nodeWrapper)
                          ? nodeWrapper[0]
                          : nodeWrapper;

                        return (
                          <div key={idx} className="rounded-md  bg-muted/20">
                            <DropdownNodeEditor
                              node={node}
                              onDelete={() => {
                                setAlldropdownTrees?.((prev) => ({
                                  ...prev,
                                  dropdownTrees: prev.dropdownTrees.filter(
                                    (_, i) => i !== idx,
                                  ),
                                }));
                              }}
                              onUpdate={(updated) => {
                                setAlldropdownTrees?.((prev) => {
                                  const newTrees = [...prev.dropdownTrees];
                                  // Always wrap in array to match any[][] type
                                  newTrees[idx] = [updated];
                                  return {
                                    ...prev,
                                    dropdownTrees: newTrees,
                                  };
                                });
                              }}
                              definedVariables={definedVariables}
                              extractKeysFromData={extractKeysFromData}
                              generateDropdownOptions={generateDropdownOptions}
                              prefillData={node}
                              actionBundles={
                                meshVersion === "2.0"
                                  ? actionBundles
                                  : (() => {
                                      const actionLogic = logicType?.find(
                                        (logic) => logic?.name === "Action",
                                      );
                                      return actionLogic?.data || [];
                                    })()
                              }
                              meshVersion={meshVersion}
                              pluginId={pluginId}
                            />
                          </div>
                        );
                      },
                    )}

                    <Button
                      onClick={() => {
                        setAlldropdownTrees?.((prev) => ({
                          ...prev,
                          dropdownTrees: [
                            ...prev.dropdownTrees,
                            [
                              {
                                label: "",
                                value: "",
                                target: "",
                                bundleId: "",
                                variableName: "",
                                labelKey: "",
                                valueKey: "",
                                availableKeys: [],
                                isDefaultOption: false,
                                targetFields: [],
                                children: [],
                              },
                            ],
                          ],
                        }));
                      }}
                    >
                      <CirclePlus size={16} className="mr-1" /> Add option for{" "}
                      <span className="ml-1 font-semibold max-w-28 truncate">
                        {fieldName}
                      </span>
                    </Button>
                  </div>
                )}

                {/*   Config API (settings) Section */}
                {!isFromVariable &&
                  !isFromAnomaly &&
                  !isApiValue &&
                  configValue && (
                    <div className="space-y-2">
                      {configDropdownTrees?.map((node, idx) => (
                        <ConfigDropdownNodeEditor
                          key={idx}
                          node={Array.isArray(node) ? node[0] : node}
                          onDelete={() => {
                            // Remove from local state
                            setConfigDropdownTrees((prev) => {
                              const updated = prev.filter((_, i) => i !== idx);
                              return updated;
                            });

                            // Remove from parent state
                            setAlldropdownTrees?.((prev) => {
                              const updated = {
                                ...prev,
                                configDropdownTrees:
                                  prev?.configDropdownTrees?.filter(
                                    (_, i) => i !== idx,
                                  ),
                              };
                              return updated;
                            });
                          }}
                          onUpdate={(updated) => {
                            // Update local state
                            setConfigDropdownTrees((prev) => {
                              const newConfigTrees = [...prev];
                              newConfigTrees[idx] = [updated];
                              return newConfigTrees;
                            });

                            // Update parent state
                            setAlldropdownTrees?.((prev) => {
                              const newConfigTrees = [
                                ...prev.configDropdownTrees,
                              ];
                              newConfigTrees[idx] = [updated];
                              return {
                                ...prev,
                                configDropdownTrees: newConfigTrees,
                              };
                            });
                          }}
                          savedSettings={savedSettings}
                          definedVariables={definedVariables}
                          extractKeysFromData={extractKeysFromData}
                          generateDropdownOptions={generateDropdownOptions}
                          prefillData={Array.isArray(node) ? node[0] : node}
                          actionBundles={actionBundles}
                          settingsBundles={settingsBundles}
                          meshVersion={meshVersion}
                          pluginId={pluginId}
                        />
                      ))}

                      <Button
                        onClick={() => {
                          const newNode = [
                            {
                              label: "",
                              value: "",
                              target: "",
                              targetFields: [],
                              children: [],
                            },
                          ];

                          // Add to local state
                          setConfigDropdownTrees((prev) => {
                            const updated = [...prev, newNode];
                            return updated;
                          });

                          // Add to parent state
                          setAlldropdownTrees?.((prev) => {
                            const updated = {
                              ...prev,
                              configDropdownTrees: [
                                ...(prev?.configDropdownTrees || []),
                                newNode,
                              ],
                            };
                            return updated;
                          });
                        }}
                      >
                        <CirclePlus size={16} className="mr-1" /> Add config
                        option for{" "}
                        <span className="ml-1 font-semibold max-w-28 truncate">
                          {fieldName}
                        </span>
                      </Button>
                    </div>
                  )}

                {/* Normal Dropdown Options */}

                {defaultDropdownOptions && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">
                      Add Dropdown Options
                    </label>
                    <div className="flex gap-2">
                      <div>
                        <input
                          type="text"
                          placeholder="Label"
                          value={dropdownInput?.label || ""}
                          onChange={(e) =>
                            setDropdownInput?.({
                              ...(dropdownInput || {}),
                              label: e.target.value,
                              value: dropdownInput?.value || "",
                            })
                          }
                          className="p-2 border rounded-md bg-transparent focus:ring-secondary focus:ring-1"
                        />
                      </div>
                      <input
                        type="text"
                        placeholder="Value"
                        value={dropdownInput?.value || ""}
                        onChange={(e) =>
                          setDropdownInput?.({
                            ...(dropdownInput || {}),
                            label: dropdownInput?.label || "",
                            value: e.target.value
                              .toLowerCase()
                              .trim()
                              .replace(/\s+/g, "_")
                              .replace(/[^a-z0-9_]/g, ""),
                          })
                        }
                        className="p-2 border rounded-md bg-transparent focus:ring-secondary focus:ring-1"
                      />
                      <Button
                        disabled={
                          !(
                            dropdownInput?.label && dropdownInput.label.trim()
                          ) ||
                          !(dropdownInput?.value && dropdownInput.value.trim())
                        }
                        onClick={handleAddDropdownOption}
                      >
                        Add Option
                      </Button>
                    </div>
                  </div>
                )}

                {/* Dropdown Option Previews */}
                {!isApiValue &&
                  dropdownOptions &&
                  dropdownOptions?.length > 0 && (
                    <div className="space-y-1 max-h-[120px] overflow-y-auto mt-2">
                      {dropdownOptions?.map((opt, idx) => (
                        <div
                          key={idx}
                          className="flex justify-between items-center border p-2 rounded-md bg-background"
                        >
                          <span>{opt.label}</span>
                          <div className="flex items-center gap-3">
                            {isFromAnomaly && (
                              <p
                                className="flex items-center gap-1 text-muted-foreground hover:text-foreground hover:bg-lightGray py-1 px-2 rounded-md cursor-pointer text-sm"
                                onClick={() =>
                                  handleCopyAnomalyField(opt.value, "anomaly")
                                }
                              >
                                <Copy size={12} />
                                <span>copy value</span>
                              </p>
                            )}
                            <Trash
                              className="cursor-pointer text-primary"
                              size={14}
                              onClick={() => handleRemoveDropdownOption?.(idx)}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
              </div>
            </div>
          )}

          {fieldType === "input" && (
            <div className="flex flex-col gap-1">
              <label> Value</label>
              <input
                type="text"
                placeholder="e.g. Block"
                value={inputDefaultValue}
                onChange={(e) =>
                  contextDispatch({
                    type: MeshPluginActions.SET_INPUT_DEFAULT_VALUE,
                    payload: e.target.value,
                  })
                }
                className="p-2 border rounded-md bg-transparent focus:ring-secondary focus:ring-1"
              />
            </div>
          )}

          {/* action submission field or submit */}
          {fieldType === "submit" && (
            <div>
              {/* check any dependency there */}
              <div className="flex items-center gap-1 mt-2 mb-2 p-1">
                <input
                  type="checkbox"
                  id="Ismultiple"
                  checked={submissionAction?.Ismultiple || false}
                  onChange={(e) =>
                    setSubmissionAction?.((prev) => ({
                      ...prev,
                      Ismultiple: e.target.checked,
                    }))
                  }
                />
                <label htmlFor="Ismultiple">
                  Submission depending on action ?
                </label>
                <InfoTooltip text="Check this for create different submission logics according to the different action types." />
              </div>

              {/* Dependency Mode UI */}
              {submissionAction?.Ismultiple && (
                <div className="space-y-4 border rounded-xl p-3 bg-lightGreen mb-4">
                  {/* Field Selector */}
                  <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium ml-1">
                      Select Action Field
                    </label>
                    <Select
                      onValueChange={(value) =>
                        setSubmissionAction?.((prev) => ({
                          ...prev,
                          defaultActionField:
                            value === "__select_action_field__" ? "" : value,
                          selectedFieldOption: "",
                        }))
                      }
                      value={
                        submissionAction?.defaultActionField ||
                        "__select_action_field__"
                      }
                    >
                      <SelectTrigger className="w-full rounded-lg">
                        <SelectValue placeholder="Select Field" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectItem value="__select_action_field__">
                            Select Action Field
                          </SelectItem>
                          {dependencyActionFields.map((field, idx) => (
                            <SelectItem key={idx} value={field.fieldName}>
                              {field.fieldName}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Configure all options from selected field */}
                  {submissionAction?.defaultActionField &&
                    (() => {
                      const selectedField = dependencyActionFields.find(
                        (f) =>
                          f.fieldName === submissionAction?.defaultActionField,
                      );
                      const fieldOptions = selectedField?.dropdownOptions || [];

                      if (fieldOptions.length === 0) {
                        return (
                          <div className="text-sm text-muted-foreground p-3 border rounded-md">
                            No options available in selected field
                          </div>
                        );
                      }

                      return (
                        <div className="max-h-[410px] overflow-y-auto">
                          <div className="text-sm font-semibold text-muted-foreground">
                            Configure Bundle & Request for each option:
                          </div>

                          {fieldOptions.map((option: any, idx: number) => {
                            // Get existing config for this option
                            const existingConfig =
                              submissionAction?.dependencyConfigs?.find(
                                (c) => c.fieldOption === option.value,
                              );

                            return (
                              <div
                                key={idx}
                                className="mt-2 border-2 border-gray-300 rounded-xl p-3 bg-muted/30"
                              >
                                <div className="font-medium text-sm flex items-center justify-between border-b pb-1 border-lightGray mb-2">
                                  <span className="text-primary font-semibold">
                                    {option.label}
                                  </span>
                                  {existingConfig ? (
                                    <span className="text-xs text-background bg-primaryGreen px-2 py-1 rounded-full flex items-center gap-2">
                                      <span>Configured</span>{" "}
                                      <CheckCheck size={14} />
                                    </span>
                                  ) : (
                                    <span className="text-xs text-muted-foreground px-2 py-1 rounded-full flex items-center gap-1">
                                      <TriangleAlert size={16} />{" "}
                                      <span>Not yet Configured.</span>
                                    </span>
                                  )}
                                </div>

                                {/* Bundle/Workflow Selector for this option */}
                                <div className="flex flex-col gap-1">
                                  <label className="text-xs font-medium">
                                    {meshVersion === "2.0"
                                      ? "Select Action Workflow"
                                      : "Select Bundle"}
                                  </label>
                                  <Select
                                    value={existingConfig?.submitBundleId || ""}
                                    onValueChange={(bundleId) => {
                                      if (bundleId === "__none__") {
                                        // Remove config for this option
                                        const updatedConfigs =
                                          submissionAction?.dependencyConfigs?.filter(
                                            (c) =>
                                              c.fieldOption !== option.value,
                                          ) || [];
                                        setSubmissionAction?.((prev) => ({
                                          ...prev,
                                          dependencyConfigs: updatedConfigs,
                                        }));
                                        return;
                                      }

                                      const updatedConfigs = [
                                        ...(submissionAction?.dependencyConfigs ||
                                          []),
                                      ];
                                      const configIndex =
                                        updatedConfigs.findIndex(
                                          (c) => c.fieldOption === option.value,
                                        );

                                      if (configIndex >= 0) {
                                        updatedConfigs[configIndex] = {
                                          ...updatedConfigs[configIndex],
                                          submitBundleId: bundleId,
                                          submitRequest: "",
                                        };
                                      } else {
                                        updatedConfigs.push({
                                          fieldOption: option.value,
                                          submitBundleId: bundleId,
                                          submitRequest: "",
                                        });
                                      }

                                      setSubmissionAction?.((prev) => ({
                                        ...prev,
                                        dependencyConfigs: updatedConfigs,
                                      }));
                                    }}
                                  >
                                    <SelectTrigger className="w-full h-9">
                                      <SelectValue
                                        placeholder={
                                          meshVersion === "2.0"
                                            ? "Select Workflow"
                                            : "Select Bundle"
                                        }
                                      />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectGroup>
                                        <SelectItem value="__none__">
                                          {meshVersion === "2.0"
                                            ? "Select workflow"
                                            : "Select bundle"}
                                        </SelectItem>
                                        {actionBundles?.map((bundle: any) => (
                                          <SelectItem
                                            key={bundle.bundleId}
                                            value={String(bundle.bundleId)}
                                          >
                                            {bundle.bundleTitle ||
                                              `Bundle ${bundle.bundleId}`}
                                          </SelectItem>
                                        ))}
                                      </SelectGroup>
                                    </SelectContent>
                                  </Select>
                                </div>

                                {/* Request Selector for this option (only for mesh version 1.0) */}
                                {meshVersion !== "2.0" &&
                                  existingConfig?.submitBundleId && (
                                    <div className="flex flex-col gap-1 mt-2">
                                      <label className="text-xs font-medium">
                                        Select Request{" "}
                                        <span className="text-muted-foreground text-xs">
                                          (Optional - leave empty to call all
                                          requests)
                                        </span>
                                      </label>
                                      <Select
                                        value={
                                          existingConfig?.submitRequest || ""
                                        }
                                        onValueChange={(requestId) => {
                                          const updatedConfigs = [
                                            ...(submissionAction?.dependencyConfigs ||
                                              []),
                                          ];
                                          const configIndex =
                                            updatedConfigs.findIndex(
                                              (c) =>
                                                c.fieldOption === option.value,
                                            );

                                          if (configIndex >= 0) {
                                            updatedConfigs[configIndex] = {
                                              ...updatedConfigs[configIndex],
                                              submitRequest: requestId,
                                            };

                                            setSubmissionAction?.((prev) => ({
                                              ...prev,
                                              dependencyConfigs: updatedConfigs,
                                            }));
                                          }
                                        }}
                                      >
                                        <SelectTrigger className="w-full h-9">
                                          <SelectValue placeholder="All Requests in Bundle" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectGroup>
                                            <SelectItem value="all_bundle_requests">
                                              All Requests in Bundle
                                            </SelectItem>
                                            {(() => {
                                              const selectedBundle =
                                                actionBundles?.find(
                                                  (b: any) =>
                                                    String(b.id) ===
                                                    existingConfig?.submitBundleId,
                                                );
                                              const requests =
                                                selectedBundle?.finalRequestData
                                                  ?.request || [];
                                              const uniqueRequests =
                                                requests.filter(
                                                  (
                                                    req: any,
                                                    index: number,
                                                    self: any[],
                                                  ) =>
                                                    index ===
                                                    self.findIndex(
                                                      (r: any) =>
                                                        r.requestId ===
                                                        req.requestId,
                                                    ),
                                                );
                                              return uniqueRequests.map(
                                                (req: any) => (
                                                  <SelectItem
                                                    key={req.requestId}
                                                    value={String(
                                                      req.requestId,
                                                    )}
                                                  >
                                                    {req.name ||
                                                      `Request ${req.requestId}`}
                                                  </SelectItem>
                                                ),
                                              );
                                            })()}
                                          </SelectGroup>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                  )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                </div>
              )}

              {/* Normal Mode UI */}
              {!submissionAction?.Ismultiple && (
                <>
                  <div className="space-y-4 border rounded-xl p-3 bg-bgAmber">
                    {/* Bundle/Workflow Selector */}
                    <div className="flex flex-col gap-2">
                      <label className="text-sm font-medium">
                        {meshVersion === "2.0"
                          ? "Select Action Workflow"
                          : "Select Action Bundle"}
                      </label>
                      <Select
                        value={submissionAction?.submitBundleId || ""}
                        onValueChange={(bundleId) => {
                          setSubmissionAction?.({
                            submitBundleId: bundleId,
                            submitRequest: "",
                          });
                        }}
                      >
                        <SelectTrigger className="w-full rounded-lg">
                          <SelectValue
                            placeholder={
                              meshVersion === "2.0"
                                ? "Select Workflow"
                                : "Select Bundle"
                            }
                          />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            {actionBundles?.map((bundle: any) => (
                              <SelectItem
                                key={bundle.bundleId}
                                value={String(bundle.bundleId)}
                              >
                                {bundle.bundleTitle ||
                                  `Bundle ${bundle.bundleId}`}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Request Selector - only show if bundle is selected (mesh version 1.0 only) */}
                    {meshVersion !== "2.0" &&
                      submissionAction?.submitBundleId && (
                        <div className="flex flex-col gap-2">
                          <label className="text-sm font-medium">
                            Select Request{" "}
                            <span className="text-muted-foreground text-xs">
                              (Optional - leave empty to call all requests)
                            </span>
                          </label>
                          <Select
                            value={submissionAction?.submitRequest || ""}
                            onValueChange={(requestId) => {
                              setSubmissionAction?.((prev) => ({
                                ...prev,
                                submitRequest: requestId,
                              }));
                            }}
                          >
                            <SelectTrigger className="w-full rounded-lg">
                              <SelectValue placeholder="All Requests in Bundle" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectGroup>
                                <SelectItem value="all_bundle_requests">
                                  All Requests in Bundle
                                </SelectItem>
                                {(() => {
                                  const selectedBundle = actionBundles?.find(
                                    (b: any) =>
                                      String(b.id) ===
                                      submissionAction?.submitBundleId,
                                  );
                                  const requests =
                                    selectedBundle?.finalRequestData?.request ||
                                    [];

                                  if (requests.length === 0) {
                                    return null;
                                  }

                                  // Filter out duplicate requestIds
                                  const uniqueRequests = requests.filter(
                                    (req: any, index: number, self: any[]) =>
                                      index ===
                                      self.findIndex(
                                        (r: any) =>
                                          r.requestId === req.requestId,
                                      ),
                                  );

                                  return uniqueRequests.map((req: any) => (
                                    <SelectItem
                                      key={req.requestId}
                                      value={String(req.requestId)}
                                    >
                                      {req.name || `Request ${req.requestId}`}
                                    </SelectItem>
                                  ));
                                })()}
                              </SelectGroup>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* settings */}

          {fieldType === "import_settings" && (
            <div className="flex flex-col gap-1">
              <label htmlFor="">Settings Configurations</label>
              <Select
                disabled={!hasSettingsWorkflows}
                onValueChange={(value: string) => {
                  setDropdownOptions?.([{ label: value, value }]);
                  contextDispatch({
                    type: MeshPluginActions.SET_INPUT_DEFAULT_VALUE,
                    payload: value,
                  });
                }}
                value={inputDefaultValue}
              >
                <SelectTrigger className="w-full">
                  <SelectValue
                    placeholder={
                      !hasSettingsWorkflows
                        ? "No settings available"
                        : "Select Settings"
                    }
                  />
                </SelectTrigger>

                <SelectContent>
                  <SelectGroup>
                    {!hasSettingsWorkflows ? (
                      <SelectItem value="__no_settings__" disabled>
                        No settings available
                      </SelectItem>
                    ) : (
                      settingsConfigurationOptions.map((config) => (
                        <SelectItem key={config.value} value={config.value}>
                          {config.label}
                        </SelectItem>
                      ))
                    )}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex gap-2  pt-2 justify-end">
            <Button
              className="bg-muted-foreground"
              onClick={() => {
                contextDispatch({
                  type: MeshPluginActions.SET_SHOW_FORM,
                  payload: false,
                });
                contextDispatch({
                  type: MeshPluginActions.SET_FIELD_NAME,
                  payload: "",
                });
                contextDispatch({
                  type: MeshPluginActions.SET_FIELD_TYPE,
                  payload: "select",
                });
                setDropdownOptions?.([]);
                setDropdownInput?.({ label: "", value: "" });
                contextDispatch({
                  type: MeshPluginActions.SET_INPUT_DEFAULT_VALUE,
                  payload: "",
                });
                setIsApiValue?.(false);
                setIsFromVariable?.(false);
                setIsFromAnomaly?.(false);
                setLabelKey("");
                setValueKey("");
                setSelectedVariableName("");
                setAvailableKeys([]);
                setEditIndex?.(null);
                setAlldropdownTrees?.({
                  dropdownTrees: [],
                  configDropdownTrees: [],
                });
                setConfigDropdownTrees([]);
                setConfigValue?.(null);
              }}
            >
              Cancel
            </Button>

            <Button
              disabled={
                !fieldName ||
                fieldType === "select" ||
                !isSubmitDependencyModeValid()
              }
              onClick={handleAddField}
            >
              Save Field
            </Button>
          </div>
        </div>
      </DialogContent>

      {/* Configuration Selector Dialog */}
      <Dialog open={showConfigSelector} onOpenChange={setShowConfigSelector}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Select Configuration</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Multiple configurations found. Please select one to refresh
              variables.
            </p>
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium">Configuration</label>
              <Select
                value={selectedConfigForRefresh}
                onValueChange={setSelectedConfigForRefresh}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select Configuration" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {getAllConfigurations().map((config) => (
                      <SelectItem key={config.uid} value={config.uid}>
                        {config.title || config.uid}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setShowConfigSelector(false);
                  setSelectedConfigForRefresh("");
                }}
              >
                Cancel
              </Button>
              <Button
                disabled={!selectedConfigForRefresh}
                onClick={handleConfigSelectionAndRefresh}
              >
                Refresh Variables
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default CreateActionField;
